import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getLiveInterviewApplicationContextStatus,
  getLiveInterviewEligibleApplications,
} from "../api/liveInterviewApi";

const normalizeScopeValue = (value) => String(value || "").trim().toLowerCase();

const formatApplicationChoice = (application) => {
  const applicationId = String(application?.application_id || "").trim();
  const status = String(application?.application_status || "").trim();
  return status ? `Application #${applicationId} · ${status}` : `Application #${applicationId}`;
};

const ApplicationContextPanel = ({
  roomId,
  bookingId = "",
  actorEmail = "",
  actorIdentity = "",
  actorRole = "",
  interviewerToken = "",
  renderReadyContent = null,
}) => {
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [listState, setListState] = useState("loading");
  const [contextState, setContextState] = useState("selection_required");
  const [error, setError] = useState("");
  const requestGenerationRef = useRef(0);
  const automaticSelectionScopeRef = useRef("");

  const normalizedRole = normalizeScopeValue(actorRole);
  const scopeKey = [
    String(roomId || "").trim(),
    String(bookingId || "").trim(),
    normalizeScopeValue(actorEmail),
    String(actorIdentity || "").trim(),
    normalizedRole,
    String(interviewerToken || "").trim(),
  ].join("::");
  const isStaff = Boolean(String(roomId || "").trim()) && normalizedRole !== "candidate";

  const selectApplication = useCallback(async (applicationId, { automatic = false } = {}) => {
    const safeApplicationId = String(applicationId || "").trim();
    if (!safeApplicationId || !isStaff) return;

    const automaticSelectionScope = `${scopeKey}::${safeApplicationId}`;
    if (automatic) {
      if (automaticSelectionScopeRef.current === automaticSelectionScope) return;
      automaticSelectionScopeRef.current = automaticSelectionScope;
    }

    const requestGeneration = requestGenerationRef.current;
    setSelectedApplicationId(safeApplicationId);
    setContextState("loading");
    setError("");
    try {
      const payload = await getLiveInterviewApplicationContextStatus(roomId, safeApplicationId, {
        interviewerToken,
      });
      if (requestGenerationRef.current !== requestGeneration) return;
      const returnedApplicationId = String(payload?.application_id || "").trim();
      if (returnedApplicationId !== safeApplicationId) {
        throw new Error("Application context could not be verified.");
      }
      setContextState(payload?.application_context_status === "ready" ? "ready" : "unavailable");
    } catch (_error) {
      if (requestGenerationRef.current !== requestGeneration) return;
      setSelectedApplicationId("");
      setContextState("error");
      setError("Application context could not be verified. Select an application again.");
    }
  }, [interviewerToken, isStaff, roomId, scopeKey]);

  useEffect(() => {
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    automaticSelectionScopeRef.current = "";
    setApplications([]);
    setSelectedApplicationId("");
    setError("");
    setContextState("selection_required");

    if (!isStaff) {
      setListState("unavailable");
      return undefined;
    }

    let cancelled = false;
    setListState("loading");
    getLiveInterviewEligibleApplications(roomId, { interviewerToken })
      .then((payload) => {
        if (cancelled || requestGenerationRef.current !== requestGeneration) return;
        const nextApplications = Array.isArray(payload?.applications) ? payload.applications : [];
        const soleApplicationId = nextApplications.length === 1
          ? String(nextApplications[0]?.application_id || "").trim()
          : "";
        setApplications(nextApplications);
        setListState(nextApplications.length ? "ready" : "unavailable");
        if (soleApplicationId) {
          // The list is already room-, booking-, candidate-, job-, and tenant-
          // scoped by the server. Revalidate the sole ID through the existing
          // private context endpoint before mounting any AI Companion content.
          void selectApplication(soleApplicationId, { automatic: true });
        } else {
          setContextState(nextApplications.length ? "selection_required" : "unavailable");
        }
      })
      .catch(() => {
        if (cancelled || requestGenerationRef.current !== requestGeneration) return;
        setApplications([]);
        setListState("error");
        setContextState("error");
        setError("Application choices could not be loaded. Please try again.");
      });

    return () => {
      cancelled = true;
    };
  }, [interviewerToken, isStaff, roomId, scopeKey, selectApplication]);

  if (!isStaff) return null;

  const hasMultipleApplications = applications.length > 1;
  const isContextReady = listState === "ready" && contextState === "ready";
  const readyContent = typeof renderReadyContent === "function"
    ? renderReadyContent({ applicationId: selectedApplicationId })
    : null;

  return (
    <section
      className={`live-interview-application-context${isContextReady && !hasMultipleApplications ? " is-context-ready" : ""}`}
      aria-label="Private interview context"
    >
      {listState === "loading" ? (
        <p className="live-interview-companion-context-status" role="status">
          Preparing interview context...
        </p>
      ) : null}
      {listState === "unavailable" ? (
        <p className="live-interview-companion-context-status" role="status">
          AI Companion is unavailable because no matching application context was found.
          <span className="live-interview-companion-context-status-hint">
            Create or correct the candidate's application for this job, including resume details and a job description,
            then reopen the interview assistant.
          </span>
        </p>
      ) : null}
      {listState === "error" ? <div className="live-interview-error" role="alert">{error}</div> : null}
      {listState === "ready" && !hasMultipleApplications && contextState === "loading" ? (
        <p className="live-interview-companion-context-status" role="status">Verifying application context...</p>
      ) : null}
      {listState === "ready" && !hasMultipleApplications && contextState === "unavailable" ? (
        <p className="live-interview-companion-context-status">Application context is unavailable.</p>
      ) : null}
      {listState === "ready" && !hasMultipleApplications && contextState === "error" ? (
        <div className="live-interview-error" role="alert">{error}</div>
      ) : null}
      {listState === "ready" && hasMultipleApplications ? (
        <div className="live-interview-application-context-body">
          <header className="live-interview-application-context-header">
            <div>
              <h3>Choose an application</h3>
              <p>Select the verified application for this interview.</p>
            </div>
          </header>
          <div className="live-interview-application-choice-list">
            {applications.map((application) => {
              const applicationId = String(application?.application_id || "").trim();
              if (!applicationId) return null;
              const selected = applicationId === selectedApplicationId;
              return (
                <button
                  key={applicationId}
                  type="button"
                  className={`live-interview-application-choice${selected ? " is-selected" : ""}`}
                  onClick={() => void selectApplication(applicationId)}
                  disabled={contextState === "loading"}
                  aria-pressed={selected}
                >
                  {formatApplicationChoice(application)}
                </button>
              );
            })}
          </div>
          {contextState === "selection_required" ? (
            <p className="live-interview-companion-context-status">Select an application to continue.</p>
          ) : null}
          {contextState === "loading" ? <p className="live-interview-companion-context-status" role="status">Verifying application context...</p> : null}
          {contextState === "unavailable" ? <p className="live-interview-companion-context-status">Application context is unavailable.</p> : null}
          {contextState === "error" ? <div className="live-interview-error" role="alert">{error}</div> : null}
        </div>
      ) : null}
      {isContextReady ? readyContent : null}
    </section>
  );
};

export default ApplicationContextPanel;
